<template>
  <form class="w-full" @submit.prevent>
    <div :class="['mb-5', { 'mb-2': !helperText }]">
      <!-- Label -->
      <label v-if="title" :for="id" :class="labelClasses">
        {{ title }}
      </label>

      <!-- Input area (flex row when action button is present) -->
      <div :class="{ flex: actionText }">
        <div class="relative w-full">
          <!-- Icon (SVG string passed in) -->
          <div
            v-if="icon"
            class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
            v-html="icon"
          ></div>

          <input
            :id="id"
            v-model="inputValue"
            :type="effectiveType"
            :aria-describedby="helperText ? `${id}-helper-text` : undefined"
            :class="inputClasses"
            :placeholder="placeholder"
            :disabled="inputDisabled"
          />

          <!-- Password Toggle Button (Eye Icon) -->
          <button
            v-if="toggleable && inputValue && !inputDisabled"
            type="button"
            class="absolute inset-y-0 right-0 flex items-center pr-3.5"
            :class="clearButtonClasses"
            tabindex="-1"
            @click="showPassword = !showPassword"
          >
            <!-- Eye icon (password hidden) -->
            <svg
              v-if="!showPassword"
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <!-- Eye-slash icon (password visible) -->
            <svg
              v-else
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
              />
            </svg>
          </button>

          <!-- Clear Button (X Icon) — only when no action button -->
          <button
            v-else-if="!actionText && inputValue && !disabled"
            type="button"
            class="absolute inset-y-0 right-0 flex items-center pr-3.5"
            :class="clearButtonClasses"
            @click="clearInput"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              :class="{
                'w-3 h-3': size === 'small',
                'w-5 h-5': size === 'base',
                'w-6 h-6': size === 'large'
              }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Action area (only when actionText is set) -->
        <template v-if="actionText">
          <!-- Cancel button -->
          <button
            v-if="cancellable && !validated"
            type="button"
            class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 whitespace-nowrap"
            @click="emit('cancel')"
          >
            Cancel
          </button>

          <!-- Action button (e.g. Validate, Save) -->
          <button
            v-if="!validated"
            type="button"
            class="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-e-lg hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 whitespace-nowrap"
            :class="{ 'opacity-50 cursor-not-allowed': buttonDisabled }"
            :disabled="buttonDisabled"
            @click="emit('action')"
          >
            <svg
              v-if="loading"
              class="inline w-4 h-4 animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.59c0 27.61-22.39 50-50 50S0 78.2 0 50.59h10c0 22.09 17.91 40 40 40s40-17.91 40-40-17.91-40-40-40V.59c27.61 0 50 22.39 50 50z"
                fill="currentColor"
              />
            </svg>
            <span v-else>{{ actionText }}</span>
          </button>

          <!-- Edit/unlock button (validated + locked) -->
          <button
            v-else-if="locked"
            type="button"
            class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 whitespace-nowrap"
            @click="emit('unlock')"
          >
            Edit
          </button>

          <!-- Green checkmark (validated + not locked) -->
          <div
            v-else
            class="flex items-center justify-center px-4 py-2.5 border border-green-500 bg-green-50 rounded-e-lg dark:bg-green-900 dark:border-green-600"
          >
            <svg
              class="w-5 h-5 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </template>
      </div>

      <!-- Helper Text -->
      <p v-if="helperText" :id="`${id}-helper-text`" :class="helperTextClasses">
        <span v-if="validation === 'success'" class="font-medium"
          >Alright!</span
        >
        <span v-if="validation === 'error'" class="font-medium">Oops!</span>
        {{ helperText }}
      </p>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";

const emit = defineEmits<{
  "update:modelValue": [value: string];
  action: [];
  cancel: [];
  unlock: [];
}>();

const props = withDefaults(
  defineProps<{
    modelValue: string | null;
    id?: string;
    title?: string;
    type?: string;
    placeholder?: string;
    helperText?: string | null;
    icon?: string | null;
    validation?: "success" | "error" | "none";
    disabled?: boolean;
    size?: "small" | "base" | "large";
    toggleable?: boolean;
    actionText?: string;
    loading?: boolean;
    validated?: boolean;
    locked?: boolean;
    cancellable?: boolean;
  }>(),
  {
    id: "",
    title: "",
    type: "text",
    placeholder: "",
    helperText: null,
    icon: null,
    validation: "none",
    disabled: false,
    size: "base",
    toggleable: false,
    actionText: "",
    loading: false,
    validated: false,
    locked: false,
    cancellable: false
  }
);

const showPassword = ref(false);

const effectiveType = computed(() => {
  if (props.toggleable && showPassword.value) return "text";
  return props.type;
});

const inputDisabled = computed(
  () => props.disabled || (props.locked && props.validated)
);

const buttonDisabled = computed(
  () => props.disabled || props.loading || !props.modelValue
);

const clearInput = () => {
  inputValue.value = "";
};

const inputValue = computed({
  get() {
    return props.modelValue ?? "";
  },
  set(val: string) {
    emit("update:modelValue", val);
  }
});

const labelClasses = computed(() => {
  const sizeClass =
    props.size === "small"
      ? "text-xs"
      : props.size === "large"
      ? "text-base"
      : "text-sm";

  if (props.validation === "success") {
    return `block mb-2 font-medium ${sizeClass} text-green-700 dark:text-green-500`;
  } else if (props.validation === "error") {
    return `block mb-2 font-medium ${sizeClass} text-red-700 dark:text-red-500`;
  } else if (props.disabled) {
    return `block mb-2 font-medium ${sizeClass} text-gray-400 dark:text-gray-500`;
  } else {
    return `block mb-2 font-medium ${sizeClass} text-gray-900 dark:text-white`;
  }
});

// Computed class for input
const inputClasses = computed(() => {
  const baseClasses = [
    `bg-gray-50 ${
      props.actionText ? "rounded-s-lg" : "rounded-lg"
    } border block w-full dark:bg-gray-700 dark:text-white dark:border-gray-600`,
    props.size === "large"
      ? "p-4 text-base"
      : props.size === "small"
      ? "p-2 text-xs"
      : "p-2.5 text-sm" // Default to base size
  ];

  if (props.validation === "success") {
    baseClasses.push(
      "bg-green-50 border-green-500 text-green-900 placeholder-green-700 focus:ring-green-500 focus:border-green-500 dark:text-green-400 dark:border-green-500 dark:placeholder-green-500"
    );
  } else if (props.validation === "error") {
    baseClasses.push(
      "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500"
    );
  } else if (props.disabled) {
    baseClasses.push(
      "bg-gray-200 text-gray-400 border-gray-300 placeholder-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-500 dark:border-gray-500 dark:placeholder-gray-600"
    );
  } else {
    baseClasses.push(
      "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 dark:placeholder-gray-400 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
    );
  }

  if (props.icon) {
    baseClasses.push("pl-10");
  } else {
    baseClasses.push("pl-3");
  }

  if (props.toggleable && props.modelValue) {
    baseClasses.push("pr-10");
  }

  return baseClasses.join(" ");
});

const helperTextClasses = computed(() => {
  const sizeClass =
    props.size === "small"
      ? "text-xs"
      : props.size === "large"
      ? "text-base"
      : "text-sm";

  const baseClasses = ["mt-2", sizeClass];

  if (props.validation === "success") {
    baseClasses.push("text-green-600 dark:text-green-500");
  } else if (props.validation === "error") {
    baseClasses.push("text-red-600 dark:text-red-500");
  } else if (props.disabled) {
    baseClasses.push("text-gray-500 dark:text-gray-400");
  } else {
    baseClasses.push("text-gray-500 dark:text-gray-400");
  }

  return baseClasses.join(" ");
});

// Computed class for clear button (X icon)
const clearButtonClasses = computed(() => {
  let buttonClass =
    "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300";

  if (props.validation === "success") {
    buttonClass =
      "text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300";
  } else if (props.validation === "error") {
    buttonClass =
      "text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300";
  }

  return buttonClass;
});
</script>

<style scoped></style>
